import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Check, Clock, Star, Shield, Zap, Users } from 'lucide-react';

const PricingSection = () => {
  const [timeLeft, setTimeLeft] = useState({
    days: 7,
    hours: 14,
    minutes: 32,
    seconds: 45
  });

  // Countdown timer effect
  useEffect(() => {
    const timer = setInterval(() => {
      setTimeLeft(prev => {
        if (prev.seconds > 0) {
          return { ...prev, seconds: prev.seconds - 1 };
        } else if (prev.minutes > 0) {
          return { ...prev, minutes: prev.minutes - 1, seconds: 59 };
        } else if (prev.hours > 0) {
          return { ...prev, hours: prev.hours - 1, minutes: 59, seconds: 59 };
        } else if (prev.days > 0) {
          return { ...prev, days: prev.days - 1, hours: 23, minutes: 59, seconds: 59 };
        }
        return prev;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  const features = [
    "Unlimited video uploads",
    "Family privacy controls", 
    "iOS app for parents",
    "HD streaming & downloads",
    "Automatic organization",
    "Email notifications",
    "30-day video retention after cancel",
    "Cloudflare enterprise security",
    "Phone & email support",
    "No setup fees"
  ];

  const testimonials = [
    {
      quote: "HiveShare saved us hours every week. Parents love the iOS app!",
      author: "Sarah M.",
      role: "Dance Studio Owner"
    },
    {
      quote: "Finally, a platform built for kids programs, not social media.",
      author: "Mike R.", 
      role: "Youth Sports Coach"
    }
  ];

  return (
    <section className="py-20 bg-gradient-to-br from-gray-50 to-blue-50">
      <div className="container mx-auto px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <Badge variant="secondary" className="bg-red-100 text-red-700 mb-4">
            <Clock className="h-4 w-4 mr-2" />
            Limited Time Offer
          </Badge>
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            Early Bird Pricing
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Lock in unlimited video sharing for your first year.
            <span className="font-semibold text-red-600"> Price goes up to $49/month after launch.</span>
          </p>
        </motion.div>

        {/* Countdown Timer */}
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="max-w-md mx-auto mb-12"
        >
          <div className="bg-gradient-to-r from-red-600 to-red-700 rounded-2xl p-6 text-white text-center shadow-xl">
            <h3 className="text-lg font-bold mb-4">Early Bird Pricing Ends In:</h3>
            <div className="grid grid-cols-4 gap-4">
              {[
                { label: 'Days', value: timeLeft.days },
                { label: 'Hours', value: timeLeft.hours },
                { label: 'Mins', value: timeLeft.minutes },
                { label: 'Secs', value: timeLeft.seconds }
              ].map((item, index) => (
                <div key={index} className="bg-white bg-opacity-20 rounded-lg p-3">
                  <div className="text-2xl font-bold">{item.value.toString().padStart(2, '0')}</div>
                  <div className="text-xs opacity-90">{item.label}</div>
                </div>
              ))}
            </div>
          </div>
        </motion.div>

        {/* Pricing Card */}
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 1 }}
          className="max-w-lg mx-auto mb-16"
        >
          <Card className="border-4 border-blue-300 shadow-2xl relative overflow-hidden">
            {/* Popular Badge */}
            <div className="absolute top-0 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
              <Badge className="bg-gradient-to-r from-blue-600 to-blue-700 text-white px-6 py-2 text-sm font-bold">
                <Star className="h-4 w-4 mr-2 fill-current" />
                Most Popular
              </Badge>
            </div>

            <CardHeader className="text-center pt-8 pb-4">
              <CardTitle className="text-3xl font-bold text-gray-900 mb-2">
                HiveShare Pro
              </CardTitle>
              <div className="mb-4">
                <div className="flex items-center justify-center gap-2 mb-2">
                  <span className="text-4xl font-bold text-gray-900">$29</span>
                  <div className="text-left">
                    <div className="text-gray-600">/month</div>
                    <div className="text-sm text-red-600 line-through">$49/month</div>
                  </div>
                </div>
                <div className="bg-green-100 text-green-800 px-3 py-1 rounded-full inline-block text-sm font-semibold">
                  Save $240 in Year 1
                </div>
              </div>
              <p className="text-gray-600">
                Everything you need to share videos securely with families
              </p>
            </CardHeader>

            <CardContent className="px-8 pb-8">
              <div className="space-y-4 mb-8">
                {features.map((feature, index) => (
                  <div key={index} className="flex items-start">
                    <Check className="h-5 w-5 text-green-600 mr-3 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-700">{feature}</span>
                  </div>
                ))}
              </div>

              <Button 
                size="lg" 
                className="w-full bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white py-4 text-lg font-semibold mb-4"
              >
                Start Free Trial - $29/month
              </Button>

              <div className="text-center text-sm text-gray-500 space-y-1">
                <p>✅ No contracts • Cancel anytime</p>
                <p>🔒 30-day video retention after cancel</p>
                <p>💳 No credit card required for trial</p>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* Trust Badges */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="max-w-4xl mx-auto mb-16"
        >
          <div className="grid md:grid-cols-3 gap-8 text-center">
            <div>
              <Shield className="h-12 w-12 text-blue-600 mx-auto mb-4" />
              <h3 className="font-bold text-gray-900 mb-2">Enterprise Security</h3>
              <p className="text-gray-600 text-sm">Cloudflare enterprise-grade CDN and security</p>
            </div>
            <div>
              <Zap className="h-12 w-12 text-green-600 mx-auto mb-4" />
              <h3 className="font-bold text-gray-900 mb-2">Lightning Fast</h3>
              <p className="text-gray-600 text-sm">Global CDN ensures videos load instantly</p>
            </div>
            <div>
              <Users className="h-12 w-12 text-purple-600 mx-auto mb-4" />
              <h3 className="font-bold text-gray-900 mb-2">Built for Families</h3>
              <p className="text-gray-600 text-sm">Privacy controls designed for activity centers</p>
            </div>
          </div>
        </motion.div>

        {/* Social Proof */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.3 }}
          className="max-w-4xl mx-auto"
        >
          <h3 className="text-2xl font-bold text-center text-gray-900 mb-8">
            Loved by Activity Center Owners
          </h3>
          <div className="grid md:grid-cols-2 gap-8">
            {testimonials.map((testimonial, index) => (
              <Card key={index} className="border border-gray-200">
                <CardContent className="p-6">
                  <div className="flex items-center mb-4">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} className="h-4 w-4 text-yellow-400 fill-current" />
                    ))}
                  </div>
                  <p className="text-gray-700 mb-4 italic">"{testimonial.quote}"</p>
                  <div>
                    <p className="font-semibold text-gray-900">{testimonial.author}</p>
                    <p className="text-sm text-gray-600">{testimonial.role}</p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </motion.div>

        {/* Urgency Reminder */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.4 }}
          className="mt-16 text-center"
        >
          <div className="bg-gradient-to-r from-red-50 to-orange-50 rounded-2xl p-8 border border-red-200 max-w-2xl mx-auto">
            <Clock className="h-8 w-8 text-red-600 mx-auto mb-4" />
            <h3 className="text-xl font-bold text-red-800 mb-2">
              Only {Math.max(23 - timeLeft.days, 3)} Early Bird Spots Left!
            </h3>
            <p className="text-red-700 mb-4">
              After launch, pricing increases to $49/month. Lock in $29/month for life.
            </p>
            <Button
              size="lg"
              className="bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 text-white px-8 py-4 font-semibold"
            >
              Secure Early Bird Pricing
            </Button>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default PricingSection;